import { useState, useEffect } from 'react'

interface UseCameraPermissionHandlerProps {
  onRejectAccessCameraPermission: () => void
}

export const useCameraPermissionHandler = ({
  onRejectAccessCameraPermission,
}: UseCameraPermissionHandlerProps) => {
  const [isPermissionGranted, setIsPermissionGranted] = useState(false)
  const [openRequiredPermissionModal, setOpenRequiredPermissionModal] =
    useState(false)
  const [permissionError, setPermissionError] = useState<string | null>(null)

  useEffect(() => {
    const checkCameraPermission = async () => {
      try {
        // Check if getUserMedia is supported
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          setPermissionError('Camera not supported in this browser')
          setOpenRequiredPermissionModal(true)
          return
        }

        // Try to get camera permission
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment', // Back camera for scanning
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        })

        // Permission granted
        setIsPermissionGranted(true)

        // Stop the stream immediately as we just needed to check permission
        stream.getTracks().forEach((track) => track.stop())
      } catch (error) {
        console.error('Camera permission error:', error)

        if (error instanceof Error) {
          if (error.name === 'NotAllowedError') {
            setPermissionError(
              'Camera access denied. Please allow camera access and try again.',
            )
          } else if (error.name === 'NotFoundError') {
            setPermissionError('No camera found on this device.')
          } else if (error.name === 'NotSupportedError') {
            setPermissionError('Camera not supported in this browser.')
          } else {
            setPermissionError(
              'Failed to access camera. Please check your browser settings.',
            )
          }
        } else {
          setPermissionError('Unknown camera error occurred.')
        }

        setIsPermissionGranted(false)
        setOpenRequiredPermissionModal(true)
      }
    }

    checkCameraPermission()
  }, [])

  const handleCancelRequiredPermissionModal = () => {
    onRejectAccessCameraPermission()
    setOpenRequiredPermissionModal(false)
  }

  const handleRetryPermission = () => {
    setOpenRequiredPermissionModal(false)
    setPermissionError(null)

    // Retry permission check
    setTimeout(() => {
      window.location.reload()
    }, 100)
  }

  return {
    isPermissionGranted,
    openRequiredPermissionModal,
    permissionError,
    handleCancelRequiredPermissionModal,
    handleRetryPermission,
  }
}
